import { trackEvent } from '../../services/analytics';
import React, { useState, useEffect, useLayoutEffect } from 'react';
import { getSessionCache, loadSessionData, sessionCacheKey } from '../../utils/sessionCache';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { addBlockedUser, removeBlockedUser, getBlockedUsers, isUserBlocked } from '../utilities/blocklist';
import {
    Box,
    Avatar,
    Typography,
    Button,
    IconButton,
    Divider,
    CircularProgress,
    Snackbar,
    Modal,
    Rating,
    Stack,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
    Business,
    CalendarMonth,
    ContactPhone,
    Edit,
    Info,
    Star,
    TableRestaurant,
    Verified,
    Work,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import Inputs from '../utilities/Inputs';
import ModalComponent from '../utilities/ModalComponent';
import InterestsSelection from '../utilities/InterestsOptions';
import MuiAlert from '@mui/material/Alert';
import Header from '../layout/Header';
import ProfileEditForm from './ProfileEditForm';
import TopAppBar from '../layout/TopAppBar';
import { requestNativeLogin } from '../../services/nativeAppBridge';
import { ActionButton, Chip, PageContainer, SectionHeader, SurfaceCard } from '../ui/ConvivialComponents';
import {
    normalizeProfilePhoneNumber,
    profilePhoneRegex,
    profileRoleOptions,
    profileSectorOptions,
} from '../../constants/profileOptions';
import {
    getUserProfile,
    sendUserPasswordReset,
    updateUserProfile,
    uploadUserAvatar,
} from '../../services/firebaseData';

export default function Profile({ completionRequired = false }) {
    const { setUserProfile } = useAuth();
    const navigate = useNavigate();
    const theme = useTheme();
    const userId = localStorage.getItem('user_id');
    const { user_requested_id: initialRequestedId } = useParams();
    const [viewState, setViewState] = useState('view');
    const [modalOpen, setModalOpen] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [modalMessage, setModalMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [userAvatarUrl, setUserAvatarUrl] = useState('');
    const [userCompleteName, setUserCompleteName] = useState('');
    const [userFirstName, setUserFirstName] = useState('');
    const [userLastName, setUserLastName] = useState('');
    const [userEmail, setUserEmail] = useState('');
    const [userPhoneNumber, setUserPhoneNumber] = useState('');
    const [rateFeedback, setRateFeedback] = useState(0);
    const [tableParticipationCount, setTableParticipationCount] = useState(0);
    const [tableOrganisatorCount, setTableOrganisatorCount] = useState(0);
    const [userCreatedAt, setUserCreatedAt] = useState('');
    const [userType, setUserType] = useState('');
    const [userGender, setUserGender] = useState('');
    const [userDateBirth, setUserDateBirth] = useState(dayjs());
    const [userDescription, setUserDescription] = useState('');
    const [userFavouriteLocation, setUserFavouriteLocation] = useState('');
    const [userPreferenceZones, setUserPreferenceZones] = useState('');
    const [userProfession, setUserProfession] = useState('');
    const [userSector, setUserSector] = useState('');
    const [userInterests, setUserInterests] = useState([]);
    const [, setSelectedImage] = useState(null);
    const [imagePreview, setImagePreview] = useState('');
    const [snackbarMessageKO, setSnackbarMessageKO] = useState('');
    const [snackbarMessageOK, setSnackbarMessageOK] = useState('');
    const [, setBlockedUsers] = useState([]);
    const userRequestedId = completionRequired ? userId : initialRequestedId || userId;
    const [fieldErrors, setFieldErrors] = useState({
        userFirstName: false,
        userLastName: false,
        userPhoneNumber: false,
        userDescription: false,
        userProfession: false,
        userSector: false,
        userFavouriteLocation: false,
        userInterests: false,
    });

    useEffect(() => {
        setBlockedUsers(getBlockedUsers());
    }, []);


    const handleBlock = (userId) => {
        addBlockedUser(userId);
        setBlockedUsers(getBlockedUsers());
    };

    const handleUnblock = (userId) => {
        removeBlockedUser(userId);
        setBlockedUsers(getBlockedUsers());
    };

    const cropToCircle = (file, size = 256) => {
        return new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.src = URL.createObjectURL(file);

            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = size;
                canvas.height = size;
                const ctx = canvas.getContext('2d');

                // draw circle mask
                ctx.beginPath();
                ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
                ctx.closePath();
                ctx.clip();

                // center-crop image (square)
                const minSide = Math.min(img.width, img.height);
                const sx = (img.width - minSide) / 2;
                const sy = (img.height - minSide) / 2;

                ctx.drawImage(img, sx, sy, minSide, minSide, 0, 0, size, size);

                canvas.toBlob((blob) => {
                    resolve(new File([blob], file.name, { type: 'image/png' }));
                }, 'image/png');
            };
        });
    };

    const handleImageChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const croppedBlob = await cropToCircle(file, 256); // size 256px

        setSelectedImage(croppedBlob);
        setImagePreview(URL.createObjectURL(croppedBlob));

        handleAvatarUpload(croppedBlob);
    };

    const handleImageClick = () => {
        if (viewState === 'edit') {
            document.getElementById('imageUpload').click(); // Trigger file input click
        } else {
            setModalOpen(true); // Open full screen modal for image
        }
    };

    const handleAvatarUpload = async (file) => {
        try {
            const avatarUrl = await uploadUserAvatar(userId, file);
            setSnackbarMessageOK('Avatar aggiornato con successo!');
            setUserAvatarUrl(avatarUrl);
        } catch (error) {
            console.error("Errore durante l'upload dell'avatar:", error);
            setSnackbarMessageKO('Errore di rete. Riprova.');
        }
    };

    const handleResetPassword = () => {
        sendUserPasswordReset(userEmail)
            .then(() => {
                setSnackbarMessageOK(
                    'Abbiamo inviato una mail di reset a ' + userEmail,
                );
                setModalOpen(false);
            })
            .catch((error) => {
                console.error('Error logging in:', error);
                setSnackbarMessageKO('Errore di rete. Riprova.');
            });
    };

    const handleUpdateProfile = async () => {
        trackEvent('action_start', { action: 'save_profile' });
        const normalizedPhoneNumber = normalizeProfilePhoneNumber(userPhoneNumber);
        const errors = {
            userFirstName: !userFirstName || userFirstName.trim() === '',
            userLastName: !userLastName || userLastName.trim() === '',
            userPhoneNumber: !profilePhoneRegex.test(normalizedPhoneNumber),
            userDescription: false,
            userProfession: false,
            userSector: false,
            userFavouriteLocation: false,
            userInterests: false,
        };
        setFieldErrors(errors);

        if (Object.values(errors).some(Boolean)) {
            setSnackbarMessageKO('Inserisci nome, cognome e un numero di telefono valido per salvare il profilo.');
            return;
        }

        setIsLoading(true);
        const nextDisplayName = [userFirstName.trim(), userLastName.trim()].join(' ');
        const profileUpdate = {
            displayName: nextDisplayName,
            firstName: userFirstName.trim(),
            lastName: userLastName.trim(),
            phoneNumber: normalizedPhoneNumber,
            email: userEmail,
            bio: userDescription,
            interests: userInterests,
            profession: userProfession,
            sector: userSector,
            favouriteLocation: userPreferenceZones,
            preferenceZones: userPreferenceZones,
        };

        try {
            const updatedProfile = await updateUserProfile(userId, profileUpdate);
            trackEvent('profile_saved');
            setUserCompleteName(nextDisplayName);
            setUserPhoneNumber(normalizedPhoneNumber);
            setModalOpen(true);
            setModalTitle("Profilo aggiornato");
            setModalMessage("Le informazioni del tuo profilo sono state salvate.");
            setUserProfile(updatedProfile);
            localStorage.setItem('profile_completed', 'confirmed');
        } catch (error) {
            setSnackbarMessageKO('Errore durante la richiesta');
            console.error('Errore durante la richiesta:', error);
            setSnackbarMessageKO('Errore di rete. Riprova.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!userId) {
            const redirectTo = userRequestedId ? `/user/${userRequestedId}` : '/user';
            const handledByNative = requestNativeLogin(redirectTo);

            if (!handledByNative) {
                navigate('/');
            }

            return;
        }

        setViewState(userRequestedId === userId ? 'edit' : 'view');
    }, [navigate, userId, userRequestedId]);

    useLayoutEffect(() => {
        let active = true;
        const cacheKey = sessionCacheKey('profile', userRequestedId, userId);
        const cached = getSessionCache(cacheKey);
        const getCurrentUserInfo = async () => {
            try {
                setIsLoading(true);

                const userData = cached || await loadSessionData(cacheKey, () => getUserProfile(userRequestedId, userId));
                if (!active) return;
                if (!userData) throw new Error('User profile not found');
                const userProfile = userData.profile || {};

                setUserType(userData.role);

                setRateFeedback(userData.stats?.averageFeedback || 0);
                setUserEmail(userData.email);
                setUserPhoneNumber(userData.phoneNumber || '');
                setUserAvatarUrl(userData.photoURL);
                setUserCompleteName(userData.displayName);
                setTableOrganisatorCount(userData.stats?.creatorCount || 0);
                setTableParticipationCount(userData.stats?.attendeeCount || 0);
                setUserCreatedAt(userData.createdAt);

                setUserGender(userProfile.gender || '');
                setUserDateBirth(
                    userProfile.dateBirth
                        ? dayjs(userProfile.dateBirth)
                        : dayjs(),
                );
                setUserDescription(userProfile.bio || '');
                setUserInterests(userProfile.interests || []);
                const displayParts = String(userData.displayName || '').trim().split(/\s+/);
                setUserFirstName(userProfile.firstName || displayParts[0] || '');
                setUserLastName(userProfile.lastName || displayParts.slice(1).join(' ') || '');
                setUserProfession(userProfile.profession || '');
                setUserSector(userProfile.sector || '');
                setUserFavouriteLocation(
                    userProfile.favouriteLocation || '',
                );
                const zones = Array.isArray(userProfile.preferenceZones)
                    ? userProfile.preferenceZones.join(', ')
                    : userProfile.favouriteLocation || '';
                setUserPreferenceZones(zones);
            } catch (error) {
                console.error('Error fetching user data:', error);
            } finally {
                if (active) setIsLoading(false);
            }
        };

        getCurrentUserInfo();
        return () => { active = false; };
    }, [userRequestedId, userId]);

    const shouldShowBio = Boolean(String(userDescription || '').trim());

    if (viewState === 'edit') {
        return (
            <>
                <ProfileEditForm
                    completionRequired={completionRequired}
                    isLoading={isLoading}
                    imagePreview={imagePreview}
                    userAvatarUrl={userAvatarUrl}
                    userCompleteName={userCompleteName}
                    setUserCompleteName={setUserCompleteName}
                    userFirstName={userFirstName}
                    setUserFirstName={setUserFirstName}
                    userLastName={userLastName}
                    setUserLastName={setUserLastName}
                    userDescription={userDescription}
                    setUserDescription={setUserDescription}
                    userProfession={userProfession}
                    setUserProfession={setUserProfession}
                    userSector={userSector}
                    setUserSector={setUserSector}
                    userInterests={userInterests}
                    setUserInterests={setUserInterests}
                    userEmail={userEmail}
                    setUserEmail={setUserEmail}
                    setUserPhoneNumber={setUserPhoneNumber}
                    userPhoneNumber={userPhoneNumber}
                    userDateBirth={userDateBirth}
                    setUserDateBirth={setUserDateBirth}
                    userGender={userGender}
                    setUserGender={setUserGender}
                    userFavouriteLocation={userFavouriteLocation}
                    setUserFavouriteLocation={setUserFavouriteLocation}
                    userPreferenceZones={userPreferenceZones}
                    setUserPreferenceZones={setUserPreferenceZones}
                    userCreatedAt={userCreatedAt}
                    tableOrganisatorCount={tableOrganisatorCount}
                    tableParticipationCount={tableParticipationCount}
                    fieldErrors={fieldErrors}
                    handleImageClick={handleImageClick}
                    handleImageChange={handleImageChange}
                    handleUpdateProfile={() => handleUpdateProfile(userId)}
                />
                <ModalComponent
                    modalOpen={modalOpen}
                    onClose={() => setModalOpen(false)}
                    title={modalTitle}
                    message={modalMessage}
                    buttons={[{ text: "Chiudi", onClick: () => setModalOpen(false) }]}
                />
                <Snackbar open={snackbarMessageKO !== ''} autoHideDuration={6000} onClose={() => setSnackbarMessageKO('')}>
                    <MuiAlert onClose={() => setSnackbarMessageKO('')} elevation={6} variant="filled" severity="error" sx={{ width: '100%' }}>
                        {snackbarMessageKO}
                    </MuiAlert>
                </Snackbar>
                <Snackbar open={snackbarMessageOK !== ''} autoHideDuration={6000} onClose={() => setSnackbarMessageOK('')}>
                    <MuiAlert onClose={() => setSnackbarMessageOK('')} elevation={6} variant="filled" severity="success" sx={{ width: '100%' }}>
                        {snackbarMessageOK}
                    </MuiAlert>
                </Snackbar>
            </>
        );
    }

    if (viewState === 'view') {
        return (
            <>
                <TopAppBar title="ConvivialApp.it" showAvatar />
                <PageContainer maxWidth="1200px" sx={{ pt: 'calc(var(--cv-top-bar-height) + 32px)' }}>
                    {isLoading ? (
                        <CircularProgress />
                    ) : (
                        <Stack spacing={3}>
                            <Box sx={{ textAlign: 'center', pt: 3, pb: 2 }}>
                                <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                    <Avatar
                                        src={imagePreview || userAvatarUrl}
                                        alt={userCompleteName}
                                        onClick={handleImageClick}
                                        sx={{
                                            width: 132,
                                            height: 132,
                                            border: '4px solid #fff',
                                            boxShadow: '0 10px 30px rgba(29,29,27,0.12)',
                                            outline: '4px solid #ffd9dd',
                                            cursor: 'pointer',
                                        }}
                                    />
                                    <Box sx={{ position: 'absolute', bottom: 0, right: 0, width: 40, height: 40, borderRadius: '50%', bgcolor: 'primary.container', color: '#ff8f9f', border: '2px solid #fff', display: 'grid', placeItems: 'center' }}>
                                        <Verified fontSize="small" />
                                    </Box>
                                </Box>
                                <Typography variant="h1" sx={{ mt: 2, color: 'text.primary', fontSize: { xs: 30, md: 36 } }}>
                                    {userCompleteName || 'Profilo Convivial'}
                                </Typography>
                                <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                                    {[userProfession, userSector].filter(Boolean).join(' & ') || 'Networking professionale'}
                                </Typography>
                                {userRequestedId === userId && (
                                    <ActionButton icon={<Edit />} onClick={() => setViewState('edit')} sx={{ mt: 2, maxWidth: 260, mx: 'auto' }}>
                                        Modifica Profilo
                                    </ActionButton>
                                )}
                            </Box>

                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '7fr 5fr' }, gap: 3 }}>
                                <SurfaceCard>
                                    <SectionHeader icon={<Info />} title="Informazioni" />
                                    <Stack spacing={1.5}>
                                        <ProfileInfoRow icon={<Work />} label="Ruolo" value={userProfession || 'Non definita'} />
                                        <ProfileInfoRow icon={<Business />} label="Azienda / settore" value={userSector || 'Non definita'} />
                                        {shouldShowBio && (
                                            <Box sx={{ pt: 0.5 }}>
                                                <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', fontWeight: 600 }}>
                                                    Bio
                                                </Typography>
                                                <Typography variant="body1" sx={{ color: 'text.secondary', mt: 0.5 }}>
                                                    {userDescription}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Stack>
                                </SurfaceCard>

                                <SurfaceCard>
                                    <SectionHeader icon={<Star />} title="Interessi" />
                                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                                        {(userInterests || []).length > 0 ? userInterests.map((interest) => (
                                            <Chip key={interest} selected>{interest}</Chip>
                                        )) : (
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                Nessun interesse pubblico aggiunto.
                                            </Typography>
                                        )}
                                    </Stack>
                                </SurfaceCard>

                                <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
                                    <SurfaceCard>
                                        <SectionHeader icon={<TableRestaurant />} title="Statistiche" />
                                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
                                            <ProfileStatTile icon={<CalendarMonth />} label="Membro da" value={formatMonthYear(userCreatedAt)} />
                                            <ProfileStatTile icon={<TableRestaurant />} label="Tavoli creati" value={tableOrganisatorCount || 0} />
                                            <ProfileStatTile icon={<ContactPhone />} label="Tavoli frequentati" value={tableParticipationCount || 0} />
                                        </Box>
                                    </SurfaceCard>
                                </Box>

                                {isUserBlocked(userRequestedId) && (
                                    <SurfaceCard sx={{ gridColumn: { xs: '1', md: '1 / -1' }, bgcolor: 'error.container' }}>
                                        <Typography variant="h3" sx={{ color: 'error.main' }}>Utente bloccato</Typography>
                                        <Typography sx={{ color: 'error.main', mt: 1 }}>
                                            Non vedrai gli eventi organizzati da questo utente e non vedra i tavoli che hai creato.
                                        </Typography>
                                    </SurfaceCard>
                                )}

                                <Box sx={{ gridColumn: { xs: '1', md: '1 / -1' } }}>
                                    <Button
                                        variant="outlined"
                                        color={isUserBlocked(userRequestedId) ? 'primary' : 'error'}
                                        fullWidth
                                        onClick={() => (isUserBlocked(userRequestedId) ? handleUnblock(userRequestedId) : handleBlock(userRequestedId))}
                                        disabled={isLoading}
                                        sx={{ borderRadius: 2.5, minHeight: 44, fontWeight: 600 }}
                                    >
                                        {isUserBlocked(userRequestedId) ? 'Sbloccare il profilo' : 'Bloccare il profilo'}
                                    </Button>
                                </Box>
                            </Box>
                        </Stack>
                    )}
                </PageContainer>
                <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
                    <Box sx={{ position: 'relative', p: 2 }}>
                        <IconButton onClick={() => setModalOpen(false)} sx={{ position: 'absolute', top: 16, right: 16, zIndex: 10 }}>
                            <CloseIcon />
                        </IconButton>
                        <img src={userAvatarUrl} alt="Profile" style={{ width: '100%', height: 'auto', borderRadius: '8px' }} />
                    </Box>
                </Modal>
                <Snackbar open={snackbarMessageKO !== ''} autoHideDuration={6000} onClose={() => setSnackbarMessageKO('')}>
                    <MuiAlert onClose={() => setSnackbarMessageKO('')} elevation={6} variant="filled" severity="error" sx={{ width: '100%' }}>
                        {snackbarMessageKO}
                    </MuiAlert>
                </Snackbar>
                <Snackbar open={snackbarMessageOK !== ''} autoHideDuration={6000} onClose={() => setSnackbarMessageOK('')}>
                    <MuiAlert onClose={() => setSnackbarMessageOK('')} elevation={6} variant="filled" severity="success" sx={{ width: '100%' }}>
                        {snackbarMessageOK}
                    </MuiAlert>
                </Snackbar>
            </>
        );
    }

    return (
        <Box sx={{ p: 2, mx: 'auto', mt: 4 }}>
            <Header
                title="Profilo"
                noGoBack={viewState === 'edit'}
                topRightText={viewState === 'edit' ? 'Salva' : false}
                topRightAction={
                    viewState === 'edit' ? () => handleUpdateProfile(userId) : false
                }
            />
            {isLoading ? (
                <CircularProgress />
            ) : (
                <Box sx={{ mt: 3, mb: 4 }}>
                    {/* Avatar, Name, and Rating */}
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Box display="flex" alignItems="center">
                            <Avatar
                                src={imagePreview || userAvatarUrl}
                                alt={userCompleteName}
                                sx={{ width: 80, height: 80, mr: 2, cursor: 'pointer' }}
                                onClick={handleImageClick}
                            />
                            <Box>
                                <Typography
                                    variant="h5"
                                    sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}
                                >
                                    {userCompleteName}
                                </Typography>
                                {rateFeedback > 0 && (
                                    <>
                                        <Box
                                            onClick={() =>
                                                navigate('/feedbacks-received/' + userRequestedId)
                                            }
                                        >
                                            <Rating
                                                value={rateFeedback}
                                                size="medium"
                                                sx={{ color: 'gold' }}
                                                readOnly
                                            />
                                        </Box>
                                    </>
                                )}
                            </Box>
                        </Box>
                        {/* Hidden file input for image upload */}
                        <input
                            type="file"
                            id="imageUpload"
                            style={{ display: 'none' }}
                            accept="image/*"
                            onChange={handleImageChange}
                        />

                        {/* Modal for Fullscreen Image View */}
                        {viewState !== 'edit' && (
                            <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
                                <Box sx={{ position: 'relative', p: 2 }}>
                                    <IconButton
                                        onClick={() => setModalOpen(false)}
                                        sx={{
                                            position: 'absolute',
                                            top: 16,
                                            right: 16,
                                            zIndex: 10,
                                        }}
                                    >
                                        <CloseIcon />
                                    </IconButton>
                                    <img
                                        src={userAvatarUrl}
                                        alt="Profile"
                                        style={{
                                            width: '100%',
                                            height: 'auto',
                                            borderRadius: '8px',
                                        }}
                                    />
                                </Box>
                            </Modal>
                        )}
                    </Box>

                    {viewState === 'view' && isUserBlocked(userRequestedId) && (
                        <Box alignItems="center">
                            <Typography variant="h6" color='red'>UTENTE BLOCCATO</Typography>
                            <Typography variant="subtitle1" sx={{ mt: 0, color: theme.palette.primary.main }}>Non vedrai gli eventi in cui questo utente è l'organizzatore, e non vedrà nessuno degli eventi che hai creato</Typography>
                        </Box>
                    )}

                    <Divider sx={{ my: 2, boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)' }} />

                    <Box alignItems="center">
                        <Typography variant="h6" sx={{ mb: -1 }}>Informazioni Professionali</Typography>
                        {viewState === 'edit' && (
                            <Typography variant="subtitle1" sx={{ mt: 0, color: theme.palette.primary.main }}>visibili pubblicamente</Typography>
                        )}
                    </Box>

                    {/* User Complete */}
                    {viewState === 'edit' ? (
                        <Box mt={0}>
                            <Inputs
                                type="text"
                                label="Nome pubblico su Convivial"
                                value={userCompleteName || ''}
                                onChange={(e) => setUserCompleteName(e.target.value)}
                                rows={0}
                                mb={0}
                            />
                        </Box>
                    ) : (
                        userCompleteName && (
                            <Box mt={2}>
                                <Typography variant="subtitle1" color="textPrimary">
                                    Nome su Convivial
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    sx={{ mb: 1 }}
                                >
                                    {userCompleteName}
                                </Typography>
                            </Box>
                        )
                    )}

                    {/* User Description */}
                    {viewState === 'edit' ? (
                        <Box mt={0}>
                            <Inputs
                                type="text"
                                required
                                label="Biografia"
                                value={userDescription || ''}
                                onChange={(e) => setUserDescription(e.target.value)}
                                rows={4}
                                error={fieldErrors.userDescription}
                                helperText={fieldErrors.userDescription ? "Campo obbligatorio" : ""}
                            />
                        </Box>
                    ) : (
                        userDescription && (
                            <Box mt={2}>
                                <Typography variant="subtitle1" color="textPrimary">
                                    Biografia:
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    sx={{ mb: 1 }}
                                >
                                    {userDescription}
                                </Typography>
                            </Box>
                        )
                    )}

                    {/* Profession */}
                    {viewState === 'edit' ? (
                        <Box mt={2}>
                            <Inputs
                                mt={0}
                                type="select"
                                label="Professione *"
                                value={userProfession}
                                onChange={(e) => setUserProfession(e.target.value)}
                                error={fieldErrors.userProfession}
                                helperText={fieldErrors.userProfession ? "Campo obbligatorio" : ""}
                                possibilities={profileRoleOptions}
                            />
                        </Box>
                    ) : (
                        userProfession && (
                            <Box mt={2}>
                                <Typography variant="subtitle1" color="textPrimary">
                                    Professione
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    sx={{ mb: 1 }}
                                >
                                    {userProfession}
                                </Typography>
                            </Box>
                        )
                    )}

                    {/* User Sector */}
                    {viewState === 'edit' ? (
                        <Box mt={2}>
                            <Inputs
                                mt={0}
                                type="select"
                                label="Settore *"
                                value={userSector}
                                onChange={(e) => setUserSector(e.target.value)}
                                error={fieldErrors.userSector}
                                helperText={fieldErrors.userSector ? "Campo obbligatorio" : ""}
                                possibilities={profileSectorOptions}
                            />
                        </Box>
                    ) : (
                        userSector && (
                            <Box mt={2}>
                                <Typography variant="subtitle1" color="textPrimary">
                                    Settore
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    sx={{ mb: 1 }}
                                >
                                    {userSector}
                                </Typography>
                            </Box>
                        )
                    )}

                    {/* User Interests */}
                    {(viewState === 'edit' || userInterests.length > 1) && (
                        <>
                            {viewState === 'edit' && (
                                <Box alignItems="center" sx={{
                                    mb: 0,
                                    mt: 1,
                                }}>
                                    <Typography
                                        sx={{
                                            ml: 2,
                                            color: '#666666',
                                        }}
                                        variant="caption"
                                    >
                                        Almeno 3 Interessi
                                    </Typography>
                                </Box>
                            )}

                            <Box mt={viewState === 'edit' ? 1 : 2}>
                                <InterestsSelection
                                    viewState={viewState}
                                    userInterests={userInterests}
                                    setUserInterests={setUserInterests}
                                />
                            </Box>
                        </>
                    )}

                    {viewState === 'edit' && (
                        <>
                            <Divider sx={{ my: 2, boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)' }} />

                            <Box alignItems="center">
                                <Typography variant="h6" sx={{ mb: -1 }}>Informazioni Personali</Typography>
                                <Typography variant="subtitle1" sx={{ mt: 0, color: theme.palette.primary.main }}>non visibili pubblicamente</Typography>
                            </Box>

                            {/* email */}
                            <Box mt={2} mb={4}>
                                <Typography variant="subtitle1" color="textPrimary">
                                    Email di iscrizione su Convivial
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="textSecondary"
                                    sx={{ mb: 1, color: theme.palette.secondary.main }}
                                >
                                    {userEmail}
                                </Typography>
                            </Box>

                            {/* Data di nascita */}
                            <Box mt={2}>
                                <Inputs
                                    mt={0}
                                    type="date"
                                    label="Data di nascita"
                                    value={userDateBirth || null}
                                    onChange={(newDate) => setUserDateBirth(newDate)}
                                    format="DD/MM/YYYY"
                                    views={['year', 'month', 'day']}
                                    disableFuture
                                />
                            </Box>

                            {/* Genere */}
                            <Box mt={2}>
                                <Inputs
                                    mt={0}
                                    type="select"
                                    label="Genere"
                                    value={userGender}
                                    onChange={(e) => setUserGender(e.target.value)}
                                    possibilities={[
                                        { value: 'Non definito', label: 'Non definito' },
                                        { value: 'Male', label: 'Uomo' },
                                        { value: 'Female', label: 'Donna' },
                                    ]}
                                />
                            </Box>

                            {/* Località preferite per gli eventi */}
                            <Box mt={0}>
                                <Inputs
                                    type="text"
                                    label="Località preferite per gli eventi *"
                                    value={userFavouriteLocation || ''}
                                    onChange={(e) => setUserFavouriteLocation(e.target.value)}
                                    rows={2}
                                    error={fieldErrors.userFavouriteLocation}
                                    helperText={fieldErrors.userFavouriteLocation ? "Campo obbligatorio" : ""}
                                />
                            </Box>
                        </>
                    )}

                    <Divider sx={{ my: 2, boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)' }} />

                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Box alignItems="center">
                            <Typography variant="h6">Statistiche del Profilo</Typography>
                        </Box>
                    </Box>

                    {/* User Statistics */}
                    <>
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <Typography
                                variant="caption"
                                sx={{ flexGrow: 1, textAlign: 'left' }}
                            >
                                Numero di tavoli a cui ha partecipato:
                            </Typography>
                            <Typography variant="h6" sx={{ flexGrow: 1, textAlign: 'right' }}>
                                {tableParticipationCount}
                            </Typography>
                        </Box>
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <Typography
                                variant="caption"
                                sx={{ flexGrow: 1, textAlign: 'left' }}
                            >
                                Numero di tavoli che ha organizzato:
                            </Typography>
                            <Typography variant="h6" sx={{ flexGrow: 1, textAlign: 'right' }}>
                                {tableOrganisatorCount}
                            </Typography>
                        </Box>
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <Typography
                                variant="caption"
                                sx={{ flexGrow: 1, textAlign: 'left' }}
                            >
                                Profilo creato il:
                            </Typography>
                            <Typography
                                variant="subtitle1"
                                sx={{ flexGrow: 1, textAlign: 'right' }}
                            >
                                {dayjs(userCreatedAt).format('DD/MM/YYYY')}
                            </Typography>
                        </Box>
                    </>

                    <Divider sx={{ my: 2, boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)' }} />

                    {/* Edit Button */}
                    {viewState === 'edit' && (
                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            onClick={() => handleUpdateProfile(userId)}
                            sx={{ mt: 2 }}
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                'Aggiorna Profilo'
                            )}
                        </Button>
                    )}
                    {/* Block Button */}
                    {viewState === 'view' && !isUserBlocked(userRequestedId) && (
                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            onClick={() => handleBlock(userRequestedId)}
                            sx={{ mt: 2 }}
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                'Bloccare il profilo'
                            )}
                        </Button>
                    )}
                    {/* Unlock Button */}
                    {viewState === 'view' && isUserBlocked(userRequestedId) && (
                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            onClick={() => handleUnblock(userRequestedId)}
                            sx={{ mt: 2 }}
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                'Sbloccare il profilo'
                            )}
                        </Button>
                    )}

                    {/* reset password button */}
                    {userType === 'EmailPassword' && viewState === 'edit' && (
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={() => handleResetPassword(userId)}
                            sx={{
                                mt: 2,
                                backgroundColor: theme.palette.secondary.main,
                            }}
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                'Reset Password'
                            )}
                        </Button>
                    )}

                    {/* Modal Edit */}
                    {viewState === 'edit' && (
                        <ModalComponent
                            modalOpen={modalOpen}
                            onClose={() => setModalOpen(false)}
                            title={modalTitle}
                            message={modalMessage}
                            buttons={[
                                {
                                    text: "Chiudi",
                                    onClick: () => setModalOpen(false)
                                }
                            ]}
                        />
                    )}

                    {/* Snackbar for ERROR notifications */}
                    <Snackbar
                        open={snackbarMessageKO !== ''}
                        autoHideDuration={6000}
                        onClose={() => setSnackbarMessageKO('')}
                    >
                        <MuiAlert
                            onClose={() => setSnackbarMessageKO('')}
                            elevation={6}
                            variant="filled"
                            severity="error"
                            sx={{ width: '100%' }}
                        >
                            {snackbarMessageKO}
                        </MuiAlert>
                    </Snackbar>

                    {/* Snackbar for SUCCESS notifications */}
                    <Snackbar
                        open={snackbarMessageOK !== ''}
                        autoHideDuration={6000}
                        onClose={() => setSnackbarMessageOK('')}
                    >
                        <MuiAlert
                            onClose={() => setSnackbarMessageOK('')}
                            elevation={6}
                            variant="filled"
                            severity="success"
                            sx={{ width: '100%' }}
                        >
                            {snackbarMessageOK}
                        </MuiAlert>
                    </Snackbar>
                </Box>
            )}
        </Box>
    );
}

function ProfileInfoRow({ icon, label, value }) {
    return (
        <Box sx={{ display: 'flex', gap: 1.5, p: 1.5, borderRadius: 2, bgcolor: 'background.surfaceLow', alignItems: 'flex-start' }}>
            <Box sx={{ color: 'secondary.main', display: 'grid', placeItems: 'center', mt: 0.25 }}>{icon}</Box>
            <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', fontWeight: 600 }}>
                    {label}
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 600 }}>
                    {value}
                </Typography>
            </Box>
        </Box>
    );
}

function formatMonthYear(value) {
    const date = value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('it-IT', { month: '2-digit', year: 'numeric' });
}

function ProfileStatTile({ icon, label, value }) {
    return (
        <Box sx={{ p: 2, bgcolor: 'background.surfaceLow', borderRadius: 2, border: '1px solid', borderColor: 'outline.variant' }}>
            <Box sx={{ color: 'secondary.main', display: 'flex', mb: 1 }}>{icon}</Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                {label}
            </Typography>
            <Typography variant="h3" sx={{ color: 'text.primary', mt: 0.5 }}>
                {value}
            </Typography>
        </Box>
    );
}
